import { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  TextField,
  Typography,
} from '@mui/material';
import { Save, SaveAs, FolderOpen, Delete } from '@mui/icons-material';
import { RootState } from '../../store/store';
import {
  clearDraft,
  dismissStaleBanner,
  draftSaved,
  hydrateDraft,
  plannerFingerprint,
  setPlannerError,
} from '../../store/slices/missionPlannerSlice';
import { missionService } from '../../services/missionService';
import { SAVED_DRAFT_PAYLOAD_VERSION, SavedDraftSummary } from '../../types/savedDraft';
import { ApiError } from '../../types/api';

/** Human, loud message for the failure modes an operator can hit. */
const describeError = (err: unknown, fallback: string): string => {
  const apiError = err as ApiError;
  const status = apiError?.status;

  // GEOFENCE_VIOLATION and REVISION_CONFLICT are both 409 and mean completely
  // different things, so status alone would tell the operator to reload when
  // what they actually need to do is move a waypoint (#451). `details.outside`
  // names the offending positions; `details.total` is the true count, which can
  // exceed the names listed — the server caps the list.
  if (apiError?.serverCode === 'GEOFENCE_VIOLATION') {
    const outside = apiError.details?.outside;
    const total = Number(apiError.details?.total ?? 0);
    const named = outside ? ` Outside the zone: ${outside}.` : '';
    const more =
      outside && total > outside.split(',').length
        ? ` (${total} in total.)`
        : '';
    return `Outside the drone's geofence — move the flagged points inside the zone, or clear the drone assignment to save an unchecked draft.${named}${more}`;
  }

  switch (status) {
    case 401:
      return 'Your session has expired — sign in again to save.';
    case 403:
      return 'You do not have permission to manage mission drafts.';
    case 404:
      return 'That draft no longer exists (it may have been deleted elsewhere).';
    case 409:
      return 'This draft was changed by another client. Reload to get the latest, then re-apply your edits.';
    default:
      return `${fallback}: ${(err as ApiError)?.message ?? String(err)}`;
  }
};

/**
 * Backend persistence controls (#299 Feature G / #321): Save / Save as / Open /
 * Delete for the operator's mission drafts, plus a banner when another client
 * saves a newer revision of the open draft. Drafts are owner-scoped server-side.
 */
export const MissionPersistencePanel: React.FC = () => {
  const dispatch = useDispatch();
  const draft = useSelector((s: RootState) => s.missionPlanner.draft);
  const returnToHome = useSelector((s: RootState) => s.missionPlanner.returnToHome);
  const savedDraftId = useSelector((s: RootState) => s.missionPlanner.savedDraftId);
  const droneId = useSelector((s: RootState) => s.missionPlanner.droneId);
  const savedDraftName = useSelector((s: RootState) => s.missionPlanner.savedDraftName);
  const savedRevision = useSelector((s: RootState) => s.missionPlanner.savedRevision);
  const staleRevisionAvailable = useSelector(
    (s: RootState) => s.missionPlanner.staleRevisionAvailable
  );

  const hasRoute = draft.waypoints.length > 0;

  const [busy, setBusy] = useState(false);
  const [nameDialogOpen, setNameDialogOpen] = useState(false);
  const [nameInput, setNameInput] = useState('');
  const [openDialogOpen, setOpenDialogOpen] = useState(false);
  const [drafts, setDrafts] = useState<SavedDraftSummary[] | null>(null);

  const payload = () => ({
    version: SAVED_DRAFT_PAYLOAD_VERSION,
    draft,
    returnToHome,
  });

  // Save: update in place when a draft is open, else prompt for a name (new).
  const onSave = async () => {
    if (savedDraftId && savedRevision !== null && savedDraftName) {
      setBusy(true);
      const fingerprint = plannerFingerprint({ draft, returnToHome, droneId });
      try {
        const saved = await missionService.updateDraft(
          savedDraftId,
          savedDraftName,
          payload(),
          savedRevision,
          droneId
        );
        dispatch(draftSaved({ id: saved.id, name: saved.name, revision: saved.revision, fingerprint }));
        dispatch(setPlannerError(null));
      } catch (err) {
        dispatch(setPlannerError(describeError(err, 'Save failed')));
      } finally {
        setBusy(false);
      }
      return;
    }
    openNameDialog();
  };

  const openNameDialog = () => {
    setNameInput(savedDraftName ?? '');
    setNameDialogOpen(true);
  };

  // Save as / first save: create a brand-new draft under the typed name.
  const onConfirmName = async () => {
    const name = nameInput.trim();
    if (!name) return;
    setNameDialogOpen(false);
    setBusy(true);
    const fingerprint = plannerFingerprint({ draft, returnToHome, droneId });
    try {
      const saved = await missionService.saveDraft(name, payload(), droneId);
      dispatch(draftSaved({ id: saved.id, name: saved.name, revision: saved.revision, fingerprint }));
      dispatch(setPlannerError(null));
    } catch (err) {
      dispatch(setPlannerError(describeError(err, 'Save failed')));
    } finally {
      setBusy(false);
    }
  };

  const onOpenList = async () => {
    setOpenDialogOpen(true);
    setDrafts(null);
    try {
      const list = await missionService.listDrafts();
      setDrafts(list.drafts);
    } catch (err) {
      setOpenDialogOpen(false);
      dispatch(setPlannerError(describeError(err, 'Could not list drafts')));
    }
  };

  const onOpenDraft = async (id: string) => {
    setOpenDialogOpen(false);
    setBusy(true);
    try {
      const saved = await missionService.loadDraft(id);
      dispatch(
        hydrateDraft({
          id: saved.id,
          name: saved.name,
          revision: saved.revision,
          draft: saved.payload.draft,
          returnToHome: saved.payload.returnToHome,
          // Restore the drone so an opened draft is checked against the same
          // fence it was saved under (#451).
          droneId: saved.drone_id ?? null,
        })
      );
      dispatch(setPlannerError(null));
    } catch (err) {
      dispatch(setPlannerError(describeError(err, 'Could not open draft')));
    } finally {
      setBusy(false);
    }
  };

  const onDelete = async () => {
    if (!savedDraftId) return;
    setBusy(true);
    try {
      await missionService.deleteDraft(savedDraftId);
      dispatch(clearDraft());
      dispatch(setPlannerError(null));
    } catch (err) {
      dispatch(setPlannerError(describeError(err, 'Delete failed')));
    } finally {
      setBusy(false);
    }
  };

  // Reload the open draft, discarding local edits, to resolve a stale banner.
  const onReloadStale = () => {
    if (savedDraftId) void onOpenDraft(savedDraftId);
  };

  return (
    <Box
      component="section"
      aria-label="Mission save and load"
      sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}
    >
      <Typography variant="subtitle2" component="h3">
        Save &amp; load
      </Typography>

      {savedDraftName && (
        <Typography variant="caption" color="text.secondary">
          Editing “{savedDraftName}” (revision {savedRevision})
        </Typography>
      )}

      {staleRevisionAvailable && (
        <Alert
          severity="warning"
          role="alert"
          action={
            <Button color="inherit" size="small" onClick={onReloadStale}>
              Reload
            </Button>
          }
          onClose={() => dispatch(dismissStaleBanner())}
        >
          A newer version of this draft was saved elsewhere.
        </Alert>
      )}

      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        <Button
          variant="outlined"
          size="small"
          startIcon={busy ? <CircularProgress size={16} /> : <Save />}
          onClick={onSave}
          disabled={busy || !hasRoute}
        >
          Save
        </Button>
        <Button
          variant="outlined"
          size="small"
          startIcon={<SaveAs />}
          onClick={openNameDialog}
          disabled={busy || !hasRoute}
        >
          Save as
        </Button>
        <Button
          variant="outlined"
          size="small"
          startIcon={<FolderOpen />}
          onClick={onOpenList}
          disabled={busy}
        >
          Open
        </Button>
        <Button
          variant="outlined"
          size="small"
          color="error"
          startIcon={<Delete />}
          onClick={onDelete}
          disabled={busy || !savedDraftId}
        >
          Delete
        </Button>
      </Box>

      {/* Name dialog for a new / Save-as draft. */}
      <Dialog open={nameDialogOpen} onClose={() => setNameDialogOpen(false)}>
        <DialogTitle>Save mission draft</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Draft name"
            fullWidth
            value={nameInput}
            onChange={(e) => setNameInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void onConfirmName();
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNameDialogOpen(false)}>Cancel</Button>
          <Button onClick={onConfirmName} disabled={!nameInput.trim()}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Open dialog listing the operator's saved drafts. */}
      <Dialog
        open={openDialogOpen}
        onClose={() => setOpenDialogOpen(false)}
        aria-labelledby="open-draft-title"
        fullWidth
      >
        <DialogTitle id="open-draft-title">Open mission draft</DialogTitle>
        <DialogContent>
          {drafts === null ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress size={24} />
            </Box>
          ) : drafts.length === 0 ? (
            <Typography color="text.secondary">No saved drafts yet.</Typography>
          ) : (
            <List>
              {drafts.map((d) => (
                <ListItem key={d.id} disablePadding>
                  <ListItemButton onClick={() => onOpenDraft(d.id)}>
                    <ListItemText
                      primary={d.name}
                      secondary={`revision ${d.revision} · ${new Date(
                        d.updated_at
                      ).toLocaleString()}`}
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialogOpen(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
